import React from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { BrowserRouter } from 'react-router-dom';
import { ApolloClient, ApolloLink, ApolloProvider, NormalizedCacheObject, Observable } from '@apollo/client';
import { Subscription } from 'zen-observable-ts';
import { onError } from '@apollo/client/link/error';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { TokenRefreshLink } from 'apollo-link-token-refresh';
import { notification } from 'antd';
import { enableMapSet } from 'immer';
import { apolloCache } from '@/apolloCache';
import App from './App';
import getAryaConfig from './utils/getAryaConfig';
import reportWebVitals from './reportWebVitals';
import { getAccessToken, setAccessToken } from './utils/accessToken';
import LocalStorage from '@/service/LocalStorage';
import AxiosProvider from '@/service/Api/AxiosProvider';
import axios from 'axios';
import { configure } from 'axios-hooks';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advanced from 'dayjs/plugin/advancedFormat';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import localeData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';
import calendarTime from 'dayjs/plugin/calendar';
import duration from 'dayjs/plugin/duration';

import './index.css';
import ApiService from '@/service/Api';
import { NotificationProvider } from '@/context/NotificationContext';
import { logout } from '@/utils/logout';
import { validateToken } from '@/utils/tokenValidation';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advanced);
dayjs.extend(minMax);
dayjs.extend(relativeTime);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(calendarTime);
dayjs.extend(duration);

enableMapSet();

if (process.env.ARYA_PLATFORM === 'production') {
  Sentry.init({
    dsn: 'https://41a53c4b1eb01eec5bbcca1ae361cd01@sentry.arya-works.com/4',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    // This sets the sample rate at 10%. You may want to change it to 100%
    // while in development and then sample at a lower rate in production.
    replaysSessionSampleRate: 0.1,
    // If you're not already sampling the entire session,
    // change the sample rate to 100% when sampling sessions where errors occur.
    replaysOnErrorSampleRate: 1.0,
  });
}

export const API_PREFIX = '/api/v1';
export const BASE_URI = String(getAryaConfig('GQL_ENDPOINT'));
export const REST_API_URI = String(getAryaConfig('REST_API_URL'));

const authLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      let handle: Subscription;
      Promise.resolve(operation)
        .then((operation) => {
          const accessToken = getAccessToken();
          if (accessToken) {
            operation.setContext({
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            });
          }
        })
        .then(() => {
          handle = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          });
        })
        .catch(observer.error.bind(observer));

      return () => {
        if (handle) handle.unsubscribe();
      };
    })
);

const refreshTokenLink = new TokenRefreshLink({
  accessTokenField: 'accessToken',
  isTokenValidOrUndefined: async () => {
    const token = getAccessToken({ withLocalStorage: true });
    if (!token) return true;

    try {
      return await validateToken(token, false);
    } catch (error) {
      return false;
    }
  },
  fetchAccessToken: () => {
    return fetch(`${REST_API_URI}/services/auth/refresh_token`, {
      method: 'POST',
      headers: {
        'x-refresh-token': `${LocalStorage.get('refresh_token')}`,
      },
    });
  },
  handleFetch: (accessToken) => {
    setAccessToken(accessToken);
    ApiService.init(accessToken);
  },
  handleError: () => {
    logout();
  },
});

const httpLink = createUploadLink({
  uri: `${BASE_URI}${API_PREFIX}`,
  credentials: 'include',
  headers: { 'Apollo-Require-Preflight': 'true' },
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors)
    graphQLErrors.forEach(({ message }) =>
      notification.error({
        message: message,
        description: 'GraphQL error',
      })
    );

  if (networkError) {
    notification.error({
      message: 'Network error',
      description: networkError.message,
    });
  }
});

const client: ApolloClient<NormalizedCacheObject> = new ApolloClient({
  link: ApolloLink.from([refreshTokenLink, errorLink, authLink, httpLink]),
  cache: apolloCache,
});

const API_URL = getAryaConfig('REST_API_URL');

export function createApiClient(baseUrl: string, token?: string) {
  return axios.create({
    baseURL: baseUrl,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: token ? `Bearer ${token}` : `Bearer ${getAccessToken()}`,
    },
    validateStatus: (status) => {
      return status >= 200 && status < 300;
    },
  });
}

export const axiosInstance = createApiClient(API_URL);
export const nonInterceptedAxiosInstance = createApiClient(API_URL);
configure({ axios: axiosInstance });

const root = document.getElementById('root');
if (root === null) throw new Error('Root container missing in index.html');
const DOMRoot = createRoot(root);

DOMRoot.render(
  <BrowserRouter>
    <ApolloProvider client={client}>
      <NotificationProvider>
        <AxiosProvider apiClient={axiosInstance}>
          <App />
        </AxiosProvider>
      </NotificationProvider>
    </ApolloProvider>
  </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
